import { ScimEntityService } from '../services/ScimEntityService';

/** Person subtypes used when resolving an id against the Person facade. */
export const PERSON_REFERENCE_SUBTYPES = [
    'Employee',
    'Contractor',
    'Student',
    'Sponsored',
    'Applicant',
    'Public',
    'Shared',
    'Vacancy',
] as const;

export interface ResolvedReferenceEntity {
    Id: string;
    DisplayName: string;
    EntityType: string;
}

function entityTypesForIdLookup(entityTypes: string[]): string[] {
    const out: string[] = [];
    for (const raw of entityTypes) {
        const t = (raw ?? '').trim();
        if (!t) continue;
        if (t.toLowerCase() === 'person') {
            out.push(...PERSON_REFERENCE_SUBTYPES);
        } else {
            out.push(t);
        }
    }
    if (out.length === 0) return ['Person'];
    return [...new Set(out)];
}

function isNotFoundError(err: unknown): boolean {
    const e = err as { response?: { status?: number }; status?: number; message?: string };
    const statusCode = e?.response?.status ?? e?.status;
    return statusCode === 404 || (e?.message ?? '').toLowerCase().includes('not found');
}

/**
 * Resolve a pasted/typed entity id by trying getEntityById for each allowed entity type.
 */
export async function lookupReferenceEntityById(
    entityService: ScimEntityService,
    rawId: string,
    entityTypes: string[]
): Promise<ResolvedReferenceEntity | null> {
    const id = (rawId ?? '').trim();
    if (!id) return null;

    const typesToTry = entityTypesForIdLookup(entityTypes);

    for (const entityType of typesToTry) {
        try {
            const full = await entityService.getEntityById(id, entityType, true, ['Id', 'DisplayName', 'EntityType']);
            const resolvedId = (full?.Id ?? full?.id ?? '').toString().trim();
            if (!resolvedId) continue;
            return {
                Id: resolvedId,
                DisplayName: (full?.DisplayName ?? full?.displayName ?? resolvedId).toString(),
                EntityType: (full?.EntityType ?? full?.entityType ?? entityType).toString(),
            };
        } catch (err) {
            if (!isNotFoundError(err)) {
                throw err;
            }
        }
    }

    return null;
}
